import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

// Services
import { MetaService, MetaEntity } from '../../../core/services/meta.service';
import { MetaFieldService } from '../../../core/services/meta-field.service';
import { MetaUiService } from '../../../core/services/meta-ui.service';

// Models
import { MetaFieldDto } from '../../../core/models/meta-field.model';
import { MetaUiReadDto } from '../../../core/models/meta-ui.model';

@Component({
      selector: 'app-meta-management',
      standalone: true,
      imports: [CommonModule, FormsModule],
      templateUrl: './meta-management.component.html'
})
export class MetaManagementComponent implements OnInit {
      // Tab State: 'entity' | 'field' | 'ui'
      activeTab: string = 'entity';

      // Shared State for Dropdowns (Searchable)
      sharedEntities: any[] = [];
      entitySearchTerm: string = '';

      // ==========================================
      // 1. ENTITY STATE & LOGIC
      // ==========================================
      entityList: MetaEntity[] = [];
      entitySearch: string = '';
      entityPage: number = 0;
      entitySize: number = 5;
      entityTotal: number = 0;

      isEntityModalOpen: boolean = false;
      isEntityEdit: boolean = false;
      entityForm: MetaEntity = this.resetEntityForm();

      // ==========================================
      // 2. FIELD STATE & LOGIC
      // ==========================================
      fieldList: MetaFieldDto[] = [];
      fieldSearch: string = '';
      fieldPage: number = 0;
      fieldSize: number = 5;
      fieldTotal: number = 0;

      isFieldModalOpen: boolean = false;
      isFieldEdit: boolean = false;
      fieldForm: Partial<MetaFieldDto> = this.resetFieldForm();

      // ==========================================
      // 3. UI SCHEMA STATE & LOGIC
      // ==========================================
      uiList: MetaUiReadDto[] = [];
      uiSearch: string = '';
      uiPage: number = 0;
      uiSize: number = 5;
      uiTotal: number = 0;
      uiLoading: boolean = false;

      isUiModalOpen: boolean = false;
      uiSelectedId: string | null = null;
      uiForm: any = { id: '', entityId: '', uiSchema: '' };
      private uiSearchSubject = new Subject<string>();

      constructor(
            private entityService: MetaService,
            private fieldService: MetaFieldService,
            private uiService: MetaUiService
      ) {
            this.uiSearchSubject.pipe(
                  debounceTime(400),
                  distinctUntilChanged()
            ).subscribe(() => {
                  this.uiPage = 0;
                  this.loadUiData();
            });
      }

      ngOnInit(): void {
            this.loadEntityData();
            this.loadFieldData();
            this.loadUiData();
            this.loadSharedEntities();
      }

      setTab(tab: string) {
            this.activeTab = tab;
      }

      // --- Shared Dropdown Logic (Searchable) ---
      loadSharedEntities(): void {
            this.entityService.getAll(0, 1000, '').subscribe({
                  next: (res: any) => this.sharedEntities = res.items,
                  error: (err) => console.error('Shared Load Error', err)
            });
      }

      get filteredSharedEntities() {
            if (!this.entitySearchTerm) return this.sharedEntities;
            return this.sharedEntities.filter(e =>
                  e.name.toLowerCase().includes(this.entitySearchTerm.toLowerCase()) ||
                  e.code.toLowerCase().includes(this.entitySearchTerm.toLowerCase())
            );
      }

      // ==========================================
      // 1. ENTITY METHODS
      // ==========================================
      loadEntityData(): void {
            this.entityService.getAll(this.entityPage, this.entitySize, this.entitySearch).subscribe({
                  next: (res: any) => {
                        this.entityList = res.items;
                        this.entityTotal = res.totalCount;
                  }
            });
      }

      applyEntityFilter(): void { this.entityPage = 0; this.loadEntityData(); }
      goEntityPage(idx: number) { this.entityPage = idx; this.loadEntityData(); }
      changeEntitySize(evt: any) { this.entitySize = +evt.target.value; this.entityPage = 0; this.loadEntityData(); }

      openEntityCreate() { this.isEntityEdit = false; this.entityForm = this.resetEntityForm(); this.isEntityModalOpen = true; }
      openEntityEdit(item: MetaEntity) { this.isEntityEdit = true; this.entityForm = { ...item, dataFlow: item.dataFlow || '{}' }; this.isEntityModalOpen = true; }
      closeEntityModal() { this.isEntityModalOpen = false; }

      saveEntity() {
            if (!this.entityForm.code || !this.entityForm.name) return;

            // ENFORCE UPPERCASE RULES FOR ENTITIES
            this.entityForm.code = this.entityForm.code.toUpperCase();
            this.entityForm.name = this.entityForm.name.toUpperCase();

            const request = this.entityForm.id
                  ? this.entityService.update(this.entityForm.id, this.entityForm)
                  : this.entityService.add(this.entityForm);

            request.subscribe(() => { this.loadEntityData(); this.loadSharedEntities(); this.closeEntityModal(); });
      }

      deleteEntity(id: string) {
            if (confirm('Delete this entity?')) this.entityService.remove(id).subscribe(() => { this.loadEntityData(); this.loadSharedEntities(); });
      }

      resetEntityForm(): MetaEntity { return { code: '', name: '', description: '', viewmode: 'grid', dataFlow: '{}' }; }

      // ==========================================
      // 2. FIELD METHODS
      // ==========================================
      loadFieldData() {
            this.fieldService.getAll(this.fieldPage, this.fieldSize, this.fieldSearch).subscribe({
                  next: (res) => { this.fieldList = res.data; this.fieldTotal = res.totalRecords; }
            });
      }

      applyFieldFilter() { this.fieldPage = 0; this.loadFieldData(); }
      goFieldPage(idx: number) { this.fieldPage = idx; this.loadFieldData(); }
      changeFieldSize(evt: any) { this.fieldSize = +evt.target.value; this.fieldPage = 0; this.loadFieldData(); }

      openFieldCreate() { this.isFieldEdit = false; this.fieldForm = this.resetFieldForm(); this.entitySearchTerm = ''; this.isFieldModalOpen = true; }
      openFieldEdit(item: MetaFieldDto) { this.isFieldEdit = true; this.fieldForm = { ...item, entityId: item.entityId }; this.entitySearchTerm = ''; this.isFieldModalOpen = true; }
      closeFieldModal() { this.isFieldModalOpen = false; }

      saveField() {
            if (!this.fieldForm.entityId) { alert("Selection Required: Please choose a valid Entity."); return; }

            const jsonFields = ['Options', 'DataSource', 'Events', 'Reference', 'Visibility', 'SoftMax', 'DependsOn', 'State'];
            const payload: any = {
                  Id: this.fieldForm.id || crypto.randomUUID(),
                  EntityId: this.fieldForm.entityId,
                  FieldId: this.fieldForm.fieldId,
                  Name: this.fieldForm.name,
                  Label: this.fieldForm.label,
                  Type: this.fieldForm.type || 'text',
                  Category: this.fieldForm.category || 'dynamic',
                  ParentField: this.fieldForm.parentField,
                  IsRequired: !!this.fieldForm.isRequired,
                  InputType: this.fieldForm.inputType,
                  Placeholder: this.fieldForm.placeholder,
                  ComputedExpression: this.fieldForm.computedExpression,
                  DisplaySource: this.fieldForm.displaySource,
                  RequiredWhen: this.fieldForm.requiredWhen,
                  Seq: this.fieldForm.seq || 0
            };

            jsonFields.forEach(field => {
                  const camelKey = (field.charAt(0).toLowerCase() + field.slice(1)) as keyof any;
                  const value = (this.fieldForm as any)[camelKey];
                  if (value !== undefined && value !== null && value !== '') {
                        if (typeof value !== 'string') { payload[field] = JSON.stringify(value); }
                        else {
                              try { JSON.parse(value); payload[field] = value; }
                              catch (e) { payload[field] = JSON.stringify(value); }
                        }
                  } else { payload[field] = null; }
            });

            const request$ = (this.isFieldEdit ? this.fieldService.update(payload.Id, payload) : this.fieldService.create(payload)) as Observable<any>;
            request$.subscribe({ next: () => { this.loadFieldData(); this.closeFieldModal(); }, error: (err: any) => console.error("Error:", err) });
      }

      deleteField(id: string) {
            if (confirm('Delete this field?')) this.fieldService.delete(id).subscribe(() => this.loadFieldData());
      }

      resetFieldForm(): Partial<MetaFieldDto> {
            return { id: '00000000-0000-0000-0000-000000000000', entityId: '', fieldId: '', name: '', label: '', type: 'text', category: 'static', entityName: '', parentField: null, isRequired: false, inputType: 'text', placeholder: '', options: null, dataSource: null, events: null, reference: null, visibility: 'visible', softMax: null, dependsOn: null, state: null, computedExpression: null, displaySource: null, requiredWhen: null, seq: 0 };
      }

      // ==========================================
      // 3. UI SCHEMA METHODS
      // ==========================================
      loadUiData() {
            this.uiLoading = true;
            this.uiService.getAll(this.uiPage, this.uiSize, this.uiSearch).subscribe({
                  next: (res) => { this.uiList = res.data; this.uiTotal = res.totalRecords; this.uiLoading = false; },
                  error: () => this.uiLoading = false
            });
      }

      applyUiFilter() { this.uiSearchSubject.next(this.uiSearch); }
      nextUiPage() { if ((this.uiPage + 1) * this.uiSize < this.uiTotal) { this.uiPage++; this.loadUiData(); } }
      prevUiPage() { if (this.uiPage > 0) { this.uiPage--; this.loadUiData(); } }
      changeUiSize() { this.uiPage = 0; this.loadUiData(); }

      openUiCreate() { this.uiSelectedId = null; this.uiForm = { id: '', entityId: '', uiSchema: '' }; this.entitySearchTerm = ''; this.isUiModalOpen = true; }
      openUiEdit(item: MetaUiReadDto) { this.uiSelectedId = item.id; this.uiForm = { ...item }; this.entitySearchTerm = ''; this.isUiModalOpen = true; }
      closeUiModal() { this.isUiModalOpen = false; this.uiSelectedId = null; }

      saveUi() {
            if (!this.uiForm.entityId) { alert("Please select an Entity"); return; }
            const payload = {
                  id: this.uiForm.id || crypto.randomUUID(),
                  entityId: this.uiForm.entityId,
                  uiSchema: typeof this.uiForm.uiSchema === 'string' ? this.uiForm.uiSchema : JSON.stringify(this.uiForm.uiSchema)
            };

            if (this.uiForm.id) {
                  this.uiService.update(this.uiForm.id, payload).subscribe({ next: () => this.closeUiModalAndReload() });
            } else {
                  this.uiService.create(payload).subscribe({ next: () => this.closeUiModalAndReload() });
            }
      }

      deleteUi(id: string) {
            if (confirm('Delete this schema?')) { this.uiLoading = true; this.uiService.delete(id).subscribe({ next: () => this.loadUiData(), error: () => this.uiLoading = false }); }
      }

      private closeUiModalAndReload() { this.isUiModalOpen = false; this.loadUiData(); }
}